import type { ColumnDef } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import { Checkbox } from '@/components/ui/checkbox';
import { ProductHoverCard } from '@/features/shared/product-hover-card';
import { formatNumber, formatNumberToVolume, formatPrice } from '@/lib/format';
import type { PurchaseOrderProduct } from '@/types/core/purchase-order/purchase-order-product';

const VOLUME_DECIMALS = 3;

function parseDecimal(value: string): number {
  const parsedValue = Number.parseFloat(value);
  return Number.isNaN(parsedValue) ? 0 : parsedValue;
}

function roundVolume(value: number): number {
  return Math.round(value * 10 ** VOLUME_DECIMALS) / 10 ** VOLUME_DECIMALS;
}

function getCbmCtn(product: PurchaseOrderProduct): number {
  const quantityPerPackage = Number.parseInt(product.quantityPerPackage, 10);
  if (quantityPerPackage <= 0) {
    return 0;
  }

  const cbmCtn = parseDecimal(product.cbmUnit) * quantityPerPackage;
  return roundVolume(cbmCtn);
}

function getCbmTotal(product: PurchaseOrderProduct): number {
  return roundVolume(
    parseDecimal(product.cbmUnit) *
      Number.parseInt(product.quantityOrdered, 10),
  );
}

function getTotalGWeight(product: PurchaseOrderProduct): number {
  const quantityPerPackage = Number.parseInt(product.quantityPerPackage, 10);
  if (quantityPerPackage <= 0) {
    return 0;
  }

  return (
    (product.importPackageInfos.weight / quantityPerPackage / 1000) *
    Number.parseInt(product.quantityOrdered, 10)
  );
}

export function usePurchaseOrderProductsTableColumns(): ColumnDef<PurchaseOrderProduct>[] {
  const { t, i18n } = useTranslation('purchaseOrder');
  const locale = i18n.language;

  return useMemo(
    () => [
      {
        id: 'selected',
        header: () => null,
        cell: ({ row }) => (
          <Checkbox
            aria-label='Select row'
            checked={row.getIsSelected()}
            onCheckedChange={(value: boolean | 'indeterminate') => {
              row.toggleSelected(!!value);
            }}
          />
        ),
        size: 24,
        enableSorting: false,
        enableHiding: false,
        meta: {
          enableColumnReordering: false,
          label: 'selected',
        },
      },
      {
        id: 'sku',
        accessorKey: 'sku',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('products.sku')} />
        ),
        cell: ({ row }) => <ProductHoverCard product={row.original.product} />,
        meta: {
          enableColumnReordering: false,
          label: t('products.sku'),
        },
      },
      {
        id: 'productSupplierId',
        accessorKey: 'productSupplierId',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.productSupplierId')}
          />
        ),
        cell: ({ row }) => <div>{row.original.productSupplierId}</div>,
        meta: {
          enableColumnReordering: false,
          label: t('products.productSupplierId'),
        },
      },
      {
        id: 'qty',
        accessorKey: 'qty',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('products.qty')} />
        ),
        cell: ({ row }) => <div>{row.original.quantityOrdered}</div>,
        meta: {
          enableColumnReordering: false,
          label: t('products.qty'),
        },
      },
      {
        id: 'unitPrice',
        accessorFn: (row) => row.price,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.unitPrice')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatPrice(
              row.original.price,
              row.original.purchaseOrder.currency.isoCode,
            )}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.unitPrice'),
        },
      },
      {
        id: 'totalPrice',
        accessorFn: (row) =>
          row.price * Number.parseInt(row.quantityOrdered, 10),
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.totalPrice')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatPrice(
              row.original.price *
                Number.parseInt(row.original.quantityOrdered, 10),
              row.original.purchaseOrder.currency.isoCode,
            )}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.totalPrice'),
        },
      },
      {
        id: 'ctnQty',
        accessorKey: 'ctnQty',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('products.ctnQty')} />
        ),
        cell: ({ row }) => (
          <div>{formatNumber(row.original.importPackageInfos.count)}</div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.ctnQty'),
        },
      },
      {
        id: 'qtyCtn',
        accessorKey: 'qtyCtn',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('products.qtyCtn')} />
        ),
        cell: ({ row }) => (
          <div>
            {formatNumber(Number.parseInt(row.original.quantityPerPackage, 10))}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.qtyCtn'),
        },
      },
      {
        id: 'cbmCtn',
        accessorKey: 'cbmCtn',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} label={t('products.cbmCtn')} />
        ),
        cell: ({ row }) => (
          <div>
            {formatNumberToVolume(getCbmCtn(row.original), {
              maximumFractionDigits: VOLUME_DECIMALS,
            })}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.cbmCtn'),
        },
      },
      {
        id: 'cbmUnit',
        accessorFn: getCbmCtn,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.cbmUnit')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatNumberToVolume(
              roundVolume(parseDecimal(row.original.cbmUnit)),
              {
                maximumFractionDigits: VOLUME_DECIMALS,
              },
            )}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.cbmUnit'),
        },
      },
      {
        id: 'cbmTotal',
        accessorFn: getCbmTotal,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.cbmTotal')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatNumberToVolume(getCbmTotal(row.original), {
              maximumFractionDigits: VOLUME_DECIMALS,
            })}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.cbmTotal'),
        },
      },
      {
        id: 'productWeight',
        accessorKey: 'productWeight',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.productWeight')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {row.original.productWeight === null
              ? '-'
              : formatNumber(row.original.productWeight / 1000, {
                  locale,
                  maximumFractionDigits: 3,
                  style: 'unit',
                  unit: 'kilogram',
                })}
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.productWeight'),
        },
      },
      {
        id: 'gWeight',
        accessorKey: 'gWeight',
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.gWeight')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatNumber(row.original.importPackageInfos.weight / 1000, {
              maximumFractionDigits: 2,
            })}{' '}
            kg
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.gWeight'),
        },
      },
      {
        id: 'totalGWeight',
        accessorFn: getTotalGWeight,
        header: ({ column }) => (
          <DataTableColumnHeader
            column={column}
            label={t('products.totalGWeight')}
          />
        ),
        cell: ({ row }) => (
          <div>
            {formatNumber(getTotalGWeight(row.original), {
              maximumFractionDigits: 2,
            })}{' '}
            kg
          </div>
        ),
        meta: {
          enableColumnReordering: false,
          label: t('products.totalGWeight'),
        },
      },
    ],
    [t, locale],
  ) satisfies ColumnDef<PurchaseOrderProduct>[];
}
